import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UpOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  getLunchOverlapConflicts,
  type LunchConflictResolution,
  type LunchMenuConflict,
} from "../../../../api/lunchMenus";
import type { CatalogView } from "../../../../api/configuration";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../../../hooks/useConfiguration";
import { useSaveLunchMenu } from "../../../../hooks/useLunchMenus";
import { useViewport } from "../../../../hooks/useViewport";
import { getApiErrorMessage } from "../../../../utils/translations";
import OrderingTopBar from "../../components/OrderingTopBar";
import { CategoryChips } from "../../components/menu/CategoryChips";
import { ItemCatalog } from "../../components/menu/ItemCatalog";
import { OrderFooter } from "../../components/menu/OrderFooter";
import { StationChips } from "../../components/menu/StationChips";
import { ConfirmActionModal } from "../../components/tables/ConfirmActionModal";
import { useOrderingMessage } from "../../components/useOrderingMessage";
import {
  lunchDraftError,
  lunchDraftToPayload,
  type LunchDraft,
  type LunchDraftItem,
} from "../lunchDraft";
import { useLunchProducts } from "../useLunchProducts";
import { LunchEditorSheet } from "./LunchEditorSheet";
import { LunchItemModal } from "./LunchItemModal";
import { LunchMenuPanel } from "./LunchMenuPanel";
import { LunchOverlapModal } from "./LunchOverlapModal";
import { LunchScheduleModal } from "./LunchScheduleModal";

interface LunchMenuEditorProps {
  /** null = ново меню (включително копие) */
  menuId: number | null;
  initialDraft: LunchDraft;
  /** savedMenuId — при успешен запис; undefined — отказ */
  onClose: (savedMenuId?: number) => void;
}

/**
 * Редакторът на обедно меню: каталогът на „Маркиране" (групи/станции,
 * търсачка, Плочки/Таблица F7, клавиатура F8, личните настройки) вляво и
 * черновата вдясно (под десктоп — лента + bottom sheet). Изборът е през
 * степера на каталога: бройка > 0 = в менюто, 0 = извън него.
 */
export const LunchMenuEditor = ({ menuId, initialDraft, onClose }: LunchMenuEditorProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useViewport();
  const message = useOrderingMessage();
  const productOf = useLunchProducts();
  const saveMutation = useSaveLunchMenu();
  const { configuration } = useConfiguration();
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();

  const [draft, setDraft] = useState<LunchDraft>(initialDraft);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedStationId, setSelectedStationId] = useState<number | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [conflicts, setConflicts] = useState<LunchMenuConflict[] | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const anyModalOpen =
    scheduleOpen || editingItemId != null || conflicts != null || discardOpen || sheetOpen;
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initialDraft),
    [draft, initialDraft],
  );

  // Каталогът мисли в бройки: избран продукт = бройка 1
  const catalogSelection = useMemo(
    () =>
      draft.items.map((item) => ({ itemId: item.itemId, quantity: 1, modifiers: [], addons: {} })),
    [draft.items],
  );
  const handleQuantityChange = useCallback(
    (itemId: number, quantity: number) =>
      setDraft((prev) => {
        const selected = prev.items.some((item) => item.itemId === itemId);
        if (quantity > 0 && !selected) {
          const mainPrice = productOf(itemId).mainPrice ?? 0;
          const newItem: LunchDraftItem = {
            itemId,
            price: mainPrice.toFixed(2),
            unitAndSizeLangs: {},
            dailyLimit: 0,
          };
          return { ...prev, items: [...prev.items, newItem] };
        }
        if (quantity <= 0 && selected)
          return { ...prev, items: prev.items.filter((item) => item.itemId !== itemId) };
        return prev;
      }),
    [productOf],
  );

  const handleCancel = useCallback(() => {
    if (dirty) setDiscardOpen(true);
    else onClose();
  }, [dirty, onClose]);

  const catalogView = configuration.catalogView;
  const handleCatalogViewChange = (view: CatalogView) => {
    if (view !== catalogView) updateUserConfiguration({ catalogView: view });
  };

  // Enter запазва — чете последната версия на `save` през ref, за да не се
  // презакача слушателят на всяка промяна в черновата
  const saveRef = useRef<() => void>(() => {});

  // F7 (Плочки/Таблица), F8 (екранна клавиатура) — както в маркирането;
  // Enter — „Запази". Esc (назад) минава през каталога (`onBack`).
  useEffect(() => {
    if (!isDesktop || anyModalOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const target = event.target as HTMLElement | null;
        // В поле за писане (видимата търсачка) Enter не е „Запази"
        if (target?.closest("input, textarea, [contenteditable='true']")) return;
        event.preventDefault();
        saveRef.current();
      } else if (event.key === "F7") {
        event.preventDefault();
        updateUserConfiguration({ catalogView: catalogView === "grid" ? "list" : "grid" });
      } else if (event.key === "F8") {
        event.preventDefault();
        setKeyboardOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop, anyModalOpen, catalogView, updateUserConfiguration]);

  const save = async (resolveConflicts?: LunchConflictResolution) => {
    const errorKey = lunchDraftError(draft, menuId != null ? initialDraft.dateFrom : null);
    if (errorKey) {
      message.error(t(errorKey));
      if (errorKey !== "lunchMenu.errorItems" && errorKey !== "lunchMenu.errorPrice")
        setScheduleOpen(true);
      return;
    }
    try {
      const saved = await saveMutation.mutateAsync({
        menuId,
        data: { ...lunchDraftToPayload(draft), resolveConflicts },
      });
      setConflicts(null);
      message.success(t("lunchMenu.saved"));
      onClose(saved.id);
    } catch (error) {
      const overlap = getLunchOverlapConflicts(error);
      if (overlap) {
        setSheetOpen(false);
        setConflicts(overlap);
        return;
      }
      message.error(getApiErrorMessage(error, t("common.error")));
    }
  };

  saveRef.current = () => {
    if (!saveMutation.isPending) void save();
  };

  const editingDraftItem = draft.items.find((item) => item.itemId === editingItemId) ?? null;
  const panelProps = {
    draft,
    saving: saveMutation.isPending,
    onEditSchedule: () => setScheduleOpen(true),
    onEditItem: setEditingItemId,
    onCancel: handleCancel,
    onSave: () => save(),
  };

  return (
    <>
      {!isDesktop && <OrderingTopBar onBack={handleCancel} />}
      <div
        className={`flex min-h-0 flex-1 overflow-hidden ${
          isDesktop ? "pb-[66px]" : "pb-[calc(56px+env(safe-area-inset-bottom,0px)*0.5)]"
        }`}
      >
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-pos-canvas">
          {isDesktop && configuration.catalogFilter === "stations" ? (
            <StationChips selectedStationId={selectedStationId} onStationSelect={setSelectedStationId} />
          ) : (
            <CategoryChips
              selectedCategoryId={selectedCategoryId}
              onCategorySelect={setSelectedCategoryId}
              collapsed={!isDesktop}
            />
          )}
          <div className="min-h-0 flex-1 overflow-hidden">
            <ItemCatalog
              categoryId={selectedCategoryId}
              selectedItemId={selectedItemId}
              onItemSelect={setSelectedItemId}
              isModalOpen={anyModalOpen}
              orderItems={catalogSelection}
              onQuantityChange={handleQuantityChange}
              onBack={handleCancel}
              view={catalogView}
              sort={configuration.catalogSort}
              stationId={isDesktop ? selectedStationId : null}
              colorBy={configuration.colorBy}
              searchFieldVisible={configuration.orderingSearchField}
              searchScope={configuration.orderingSearchScope}
              onExitCategory={() => setSelectedCategoryId(null)}
              keyboardOpen={keyboardOpen}
              keyboardLayout={configuration.keyboardLayout}
              onKeyboardClose={() => setKeyboardOpen(false)}
            />
          </div>
        </main>
        {isDesktop && <LunchMenuPanel {...panelProps} />}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-50 flex flex-col">
        {isDesktop ? (
          <OrderFooter
            onBack={handleCancel}
            catalogView={catalogView}
            onCatalogViewChange={handleCatalogViewChange}
            keyboardOpen={keyboardOpen}
            onToggleKeyboard={() => setKeyboardOpen((open) => !open)}
            contextTitle={t(menuId == null ? "lunchMenu.creatingTitle" : "lunchMenu.editingTitle")}
          />
        ) : (
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="flex w-full cursor-pointer items-center gap-2.5 border-0 border-t border-pos-line2 bg-pos-accent px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px)*0.5)] pt-3 transition-colors hover:bg-pos-accent-ink"
          >
            <span className="grid h-[26px] min-w-[26px] place-items-center rounded-full bg-white/25 px-1 font-pos-mono text-[13px] font-bold text-white">
              {draft.items.length}
            </span>
            <span className="text-sm font-semibold text-white">{t("lunchMenu.reviewAndSave")}</span>
            <UpOutlined className="ml-auto text-[13px] text-white" />
          </button>
        )}
      </div>

      <LunchEditorSheet open={sheetOpen && !isDesktop} onClose={() => setSheetOpen(false)}>
        <LunchMenuPanel {...panelProps} inSheet />
      </LunchEditorSheet>

      <LunchScheduleModal
        open={scheduleOpen}
        schedule={draft}
        onClose={() => setScheduleOpen(false)}
        onApply={(schedule) => {
          setDraft((prev) => ({ ...prev, ...schedule }));
          setScheduleOpen(false);
        }}
      />
      <LunchItemModal
        draftItem={editingDraftItem}
        product={editingDraftItem ? productOf(editingDraftItem.itemId) : null}
        onClose={() => setEditingItemId(null)}
        onApply={(updated) => {
          setDraft((prev) => ({
            ...prev,
            items: prev.items.map((item) => (item.itemId === updated.itemId ? updated : item)),
          }));
          setEditingItemId(null);
        }}
        onRemove={(itemId) => {
          handleQuantityChange(itemId, 0);
          setEditingItemId(null);
        }}
      />
      <LunchOverlapModal
        conflicts={conflicts}
        newDateFrom={draft.dateFrom}
        saving={saveMutation.isPending}
        onClose={() => setConflicts(null)}
        onResolve={(resolution) => save(resolution)}
      />
      <ConfirmActionModal
        open={discardOpen}
        title={t("common.unsavedTitle")}
        message={t("common.unsavedDesc")}
        confirmLabel={t("common.unsavedLeave")}
        cancelLabel={t("common.unsavedStay")}
        onCancel={() => setDiscardOpen(false)}
        onConfirm={() => onClose()}
      />
    </>
  );
};
